import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Box, IconButton, TextField, Typography} from '@mui/material';
import AurBreadcrumb from '../../common/aur-breadcrumb/AurBreadCrumb';
import {t} from 'i18next';
import AurButton from '../../common/aur-button/AurButton';
import AurIcon, {IconColors, IconNames} from '../../common/aur-icon/AurIcon';
import {
  AurDropdownButton,
  DropdownIconTypes,
} from '../../common/aur-button/AurDropdownButton';
import {ReactComponent as ExchangeIcon} from '../../../../src/assests/svg/icon_shift.svg';
import {ReactComponent as AddIcon} from '../../../../src/assests/svg/add.svg';
import AurSvgIcon from '../../common/aur-svg-icon/AurSvgIcon';
import {ReactComponent as ErrorIcon} from '../../../assests/svg/error-field-icon.svg';
import {ReactComponent as BackgroundBlur} from '../../../assests/svg/backgroundBlur.svg';
import {
  DataGridPro,
  getGridDateOperators,
  GridCellParams,
  GridRenderHeaderFilterProps,
  GridFilterModel,
  useGridApiRef,
  GridApiPro,
  GridColDef,
} from '@mui/x-data-grid-pro';
import {ecaExportResponseType} from '../../eca-doc-list/eca-doc-list.util';
import {useDispatch, useSelector} from 'react-redux';
import {
  EciHomePageTabs,
  TABLE_PAGE_SIZE_OPTION,
} from '../../../constants/constants';
import AurPagination from '../../common/aur-pagination/AurPagination';
import {RootState} from '../../../redux/store';
import ProjectSwitcherModal from '../../ecapages/eca-overview/eca-overlay/ProjectSwitcherModal';
import {setModalStatus} from '../../../redux/slices/invoiceModalSlice';
import AurDataGridCellWrapper from '../../common/aur-data-grid/AurDataGridCellWrapper';
import {
  downloadResponseAsCSV,
  getProjectReportFileName,
  getUTCDateTimeForDisplay,
  scrollToGridView,
} from '../../../utils/utils';
import FieldPopoverGridFilter, {
  FieldFilterGridData,
} from '../../pages/field-management/field-table/FieldPopoverGridFilter';
import AurTooltip from '../../common/aur-tooltip/AurTooltip';
import {useNavigate} from 'react-router-dom';
import {DownloadExportCsv, getExportFields} from '../../../api/EcaServices';
import {toODataQueryExport} from '../../common/aur-data-grid-pro/useServerSideDataGrid';
import AurToast from '../../common/aur-toast/AurToast';

import {
  setExportId,
  setExportName,
  setExportTab1Params,
  setSelectedExport,
  setSelectedExportId,
} from '../../../redux/slices/addExportSlice';
import AurInfographicSeperator from '../../common/aur-infographic/AurInfographicSeperator';
import {debounce, isEmpty} from 'lodash';
import AurPopup from '../../common/aur-popup/AurPopup';
import {ReactComponent as HeadingIcon} from '../../../assests/svg/warning-blue-icon.svg';
import {updateMarkAsLoaded} from '../../../api/EcaManagementService';
import AurLoadingIndicator from '../../common/aur-loading-indicator/AurLoadingIndictor';
import AurDataGridNoRowsOverlay from '../../common/aur-data-grid-no-rows-overlay/AurDataGridNoRowsOverlay';
import {
  PAGE_TYPE,
  useManageMenuByPermissions,
} from '../../../hooks/permissions/useManageMenuByPermissions';
import {
  PAGE_NAME,
  usePermissionsByPage,
} from '../../../hooks/permissions/usePermissionsByPage';
import {SecurePage} from '../../pages/SecurePage';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_FILE_EXTENTIONS,
  GA_REGIONS,
} from '../../../constants/analyticsConstants';
import {useAnalytics} from '../../../hooks/useAnalytics';

interface StatusType {
  InProgress: string;
  Failed: string;
  Completed: string;
  CompletedWithError: string;
  Queued: string;
}

interface CustomHeaderFilterCellProps {
  params: GridRenderHeaderFilterProps;
  apiRef: React.MutableRefObject<GridApiPro>;
}

const CustomHeaderFilterCell = (props: CustomHeaderFilterCellProps) => {
  const params = props.params;
  const apiRef = props.apiRef;
  const filterModel = apiRef.current?.state?.filter?.filterModel;

  const {trackGAEvent} = useAnalytics();

  // Find the current filters applied to this column
  const currentFieldFilters = useMemo(
    () => filterModel.items.filter(({field}) => field === params.colDef.field),
    [params.colDef.field, filterModel.items]
  );

  const debouncedTrackRef = useRef(
    debounce((columnName: string) => {
      trackGAEvent({
        eventName: GA_EVENT_NAMES.FILTER,
        click: {
          name: columnName,
          type: GA_CLICK_TYPES.INPUT,
          region: GA_REGIONS.BODY,
          action: t('gaTracking.export.filteredList'),
        },
      });
    }, 1000)
  );

  return (
    <TextField
      {...params.InputComponentProps}
      inputRef={params.inputRef}
      value={params.item.value ?? ''}
      onChange={(event) => {
        debouncedTrackRef.current(
          String(params.colDef.headerName ?? params.colDef.field)
        );
        apiRef.current.upsertFilterItem({
          ...params.item,
          value: event.target.value,
        });

        isEmpty(event.target.value) &&
          currentFieldFilters[0] &&
          apiRef.current.deleteFilterItem(currentFieldFilters[0]);
      }}
      placeholder={params.InputComponentProps?.placeholder ?? ''}
      variant="standard"
      fullWidth
      inputProps={{
        tabIndex: 0,
        ...params.InputComponentProps?.inputProps,
      }}
    />
  );
};

const ExportManagementLanding: React.FC = () => {
  const dispatch = useDispatch();

  const {trackGAEvent} = useAnalytics();
  const apiRef = useGridApiRef();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [selectionModel, setSelectionModel] = useState<any[]>([]);
  const [isDownloading, setIsDownloading] = useState(false);
  const [rows, setRows] = useState<ecaExportResponseType[]>([]);
  const [totalCount, setTotalCount] = useState(0); //added a static number for testing
  const lastODataQueryTagAudit = useRef<string | null>(null);
  const [exportSuccessPopup, setExportSuccessPopup] = useState<boolean>(false);
  const [filterCount, setFilterCount] = useState<number>(0);
  const clientId = useSelector(
    (state: RootState) => state.selectedClient
  ).clientId;
  const [reqLoading, setReqLoading] = useState<boolean>(false);

  const {manageMenus} = useManageMenuByPermissions(PAGE_TYPE.Export);
  const {hasExportPerm} = usePermissionsByPage(PAGE_NAME.EXPORT_MANAGEMENT);

  const {projectId: selectedProjectId, selectedProjectName: projectName} =
    useSelector((state: RootState) => state.reports);

  const statusText: StatusType = {
    InProgress: t('common.statusTypes.complete'),
    Failed: t('common.statusTypes.failed'),
    Completed: t('common.statusTypes.completed'),
    CompletedWithError: t('common.statusTypes.completedWithErrors'),
    Queued: t('common.statusTypes.queued'),
  };

  const exportCreated = useSelector(
    (state: RootState) => state.addExport.selectedExport
  );
  const createExportName = useSelector(
    (state: RootState) => state?.addExport?.exportTabsValues
  )?.name;

  const [showExportedPopup, setShowExportedPopup] = useState<boolean>(false);
  const [markToExportId, setMarkToExportId] = useState<string>('');

  useEffect(() => {
    if (exportCreated) {
      setExportSuccessPopup(true);
      setTimeout(() => {
        setExportSuccessPopup(false);
      }, 5000);
      dispatch(setSelectedExport(false));
    }
  }, [dispatch, exportCreated]);

  const fieldTypeDataArray: FieldFilterGridData[] = [
    {id: 1, value: 'InProgress', label: t('common.statusTypes.inProgress')},
    {id: 2, value: 'Failed', label: t('common.statusTypes.failed')},
    {id: 3, value: 'Completed', label: t('common.statusTypes.completed')},
    {
      id: 4,
      value: 'CompletedWithError',
      label: t('common.statusTypes.completedWithError'),
    },
    {id: 5, value: 'Queued', label: t('common.statusTypes.queued')},
  ];

  const ExoprtTypeDataArray: FieldFilterGridData[] = [
    {
      id: 1,
      value: 'Review',
      label: t('eca.exportManagement.exportedForReview'),
    },
    {
      id: 2,
      value: 'Analytics',
      label: t('eca.exportManagement.exportedForAnalytics'),
    },
  ];
  const breadCrumbItems = useMemo(() => {
    const baseItems: {label: string; path?: string}[] = [
      {
        label: t('eca.ecaHomepage.coreEciHome'),
        path: `/eci-home-page?tab=${EciHomePageTabs.ECI_OVERVIEW}`,
      },
      {
        label: t('eca.exportManagement.labelExport'),
      },
    ];
    return baseItems;
  }, []);

  const renderExportType = (key: string): string => {
    const exportTypeMapping: Record<string, string> = {
      Review: 'Exported for Review',
      Analytics: 'Exported for Analytics',
    };
    return exportTypeMapping[key];
  };
  const computeStatus = (params: {
    value: keyof StatusType;
    row: any;
    errorMessage: string;
  }) => {
    const status =
      params.value === 'InProgress'
        ? `${params.row.percentageComplete}% Completed`
        : statusText[params.value];
    return (
      <>
        <Typography
          className={`typo-text body3_size ${params.value === 'InProgress' ? 'status-complete' : 'status-text-width'}`}
          tabIndex={0}
        >
          {status}
        </Typography>
        {['CompletedWithError', 'Failed'].includes(params.value) && (
          <AurTooltip
            title={params?.row?.errorMessage || 'No error message'}
            tabIndex={0}
          >
            <ErrorIcon className="error-icon" />
          </AurTooltip>
        )}
      </>
    );
  };
  /**  Copies text to clipboard */
  const handleCopyToClipboard = (e: any, text: string) => {
    e.stopPropagation();
    navigator.clipboard.writeText(text).then(
      () => console.log('Copied to clipboard:', text),
      (err) => console.error('Failed to copy:', err)
    );
  };

  const handlePopupContinue = async () => {
    const params = `?clientId=${clientId}&projectId=${selectedProjectId}`;
    try {
      const results = await updateMarkAsLoaded(params, markToExportId);
      if (results.status == 200) {
        const successResponse = results?.data?.meta?.status;
        if (successResponse == 'Success') {
          setShowExportedPopup(false);
          const filterModel = apiRef.current.state.filter.filterModel;
          const updatedGridFilterModel = filterModel
            ? {
                ...filterModel,
                items: filterModel?.items.map((item: any) => ({
                  ...item,
                  field:
                    item.field.charAt(0).toUpperCase() + item.field.slice(1),
                })),
              }
            : filterModel;
          const sortModel = apiRef.current.state.sorting.sortModel;
          const ODataQuery = toODataQueryExport(
            updatedGridFilterModel,
            sortModel,
            {
              page: currentPage,
              pageSize: itemsPerPage,
            }
          );
          loadExportList(ODataQuery);
        }
      }
    } catch (e) {
      console.log(e);
    }
  };

  const handleExportFields = useCallback(
    async (ODataQuery: string) => {
      if (lastODataQueryTagAudit.current === ODataQuery) return; //  Prevent duplicate calls
      lastODataQueryTagAudit.current = ODataQuery;

      const filterModel: GridFilterModel =
        apiRef.current.state.filter.filterModel;
      const uniqueFields = new Set(filterModel.items.map((item) => item.field));
      setFilterCount(uniqueFields.size);
      loadExportList(ODataQuery);
    },
    [apiRef, clientId, selectedProjectId]
  );
  //

  const loadExportList = async (ODataQuery: any) => {
    setReqLoading(true);
    try {
      const params = `clientId=${clientId}&projectId=${selectedProjectId}&${ODataQuery}`;
      const response = await getExportFields(params);
      const res = response?.data?.data;
      const data: ecaExportResponseType[] = res || [];
      setTotalCount(response?.data?.meta?.pagination?.totalItems ?? 0);
      const rowsWithId: ecaExportResponseType[] = data?.map((row, index) => ({
        ...row,
        id: index + 1,
      }));
      setRows(rowsWithId);
      scrollToGridView('aur-grid-pro-export');
    } catch (error) {
      setReqLoading(false);
      console.error('Error fetching audit report:', error);
    } finally {
      setReqLoading(false);
    }
  };
  const sortFilterField = (arr: GridFilterModel) => {
    return arr.items.slice().sort((a, b) => {
      if (a.field < b.field) {
        return -1;
      } else if (a.field > b.field) {
        return 1;
      } else {
        return 0;
      }
    });
  };
  const fetchData = useCallback(() => {
    if (!hasExportPerm) return;
    if (!apiRef?.current || !selectedProjectId || !clientId) return;

    const filterModel = apiRef.current.state.filter.filterModel;
    const gridFilterModelSort = sortFilterField(filterModel);
    filterModel.items = gridFilterModelSort;
    const updatedGridFilterModel = filterModel
      ? {
          ...filterModel,
          items: filterModel?.items.map((item: any) => ({
            ...item,
            field: item.field.charAt(0).toUpperCase() + item.field.slice(1),
          })),
        }
      : filterModel;
    const sortModel = apiRef.current.state.sorting.sortModel;
    const ODataQuery = toODataQueryExport(updatedGridFilterModel, sortModel, {
      page: currentPage,
      pageSize: itemsPerPage,
    });
    handleExportFields(ODataQuery);
  }, [selectedProjectId, clientId, currentPage, itemsPerPage, hasExportPerm]);

  useEffect(() => {
    if (!hasExportPerm) return;
    lastODataQueryTagAudit.current = null;
    fetchData();
  }, [selectedProjectId, clientId, currentPage, itemsPerPage, hasExportPerm]);

  useEffect(() => {
    if (!apiRef?.current || !hasExportPerm) return;

    const debouncedFetchData = debounce(() => {
      fetchData();
    }, 1000);
    const handleFilterChange = () => {
      if (currentPage === 1) {
        debouncedFetchData();
      } else {
        setCurrentPage(1);
      }
    };
    const unsubscribe = apiRef.current.subscribeEvent(
      'filterModelChange',
      handleFilterChange
    );
    return () => {
      unsubscribe();
      debouncedFetchData.cancel();
    };
  }, [apiRef, fetchData, hasExportPerm]);

  const ExportToClickHandler = (e: any, params: any) => {
    e.stopPropagation();
    setMarkToExportId(params.row.exportId);
    setShowExportedPopup(true);
  };

  const closePopupModalHandler = () => {
    setShowExportedPopup(false);
  };

  const onApplyFiltersDropdown = (columnField: string) => {
    trackGAEvent({
      eventName: GA_EVENT_NAMES.FILTER,
      click: {
        name: columnField,
        type: GA_CLICK_TYPES.DROPDOWN,
        region: GA_REGIONS.BODY,
        action: t('gaTracking.export.filteredList'),
      },
    });
  };

  const columns: GridColDef<ecaExportResponseType>[] = [
    {
      field: 'exportName',
      headerName: t('eca.exportManagement.exportName'),
      minWidth: 214,
      flex: 1,
      sortable: true,
      filterable: true,
      renderCell: (params) => {
        const promotionNumber = params?.row?.promotionNumber || '--';
        return (
          <div>
            <Typography
              className="typo-text body3_size text-color-89A6FF"
              tabIndex={0}
            >
              {params?.row?.exportName}
            </Typography>
            {params.row?.exportType?.toLowerCase() === 'analytics' ? null : (
              <AurTooltip
                title={promotionNumber === '--' ? '' : promotionNumber}
              >
                <Typography className="typo-text body4_size text-color-A0AEC2 promotion-number-text">
                  {t('common.promotionNumber')}: {promotionNumber}
                </Typography>
              </AurTooltip>
            )}
          </div>
        );
      },
      renderHeaderFilter: (params) => (
        <CustomHeaderFilterCell params={params} apiRef={apiRef} />
      ),
    },
    {
      field: 'exportType',
      headerName: t('eca.exportManagement.exportType'),
      minWidth: 221,
      flex: 1,
      sortable: true,
      renderCell: (params) => (
        <Typography
          className="typo-text body3_size text-color-F2F4F7"
          tabIndex={0}
        >
          {renderExportType(params.value)}
        </Typography>
      ),
      renderHeaderFilter: () => (
        <FieldPopoverGridFilter
          key={'exportType'}
          apiRef={apiRef}
          onApplyFilters={() =>
            onApplyFiltersDropdown(t('eca.exportManagement.exportType'))
          }
          columnField={'exportType'}
          type={'options'}
          dataArray={ExoprtTypeDataArray}
        />
      ),
    },
    {
      field: 'status',
      headerName: t('eca.exportManagement.status'),
      minWidth: 172,
      flex: 1,
      sortable: true,
      renderCell: (params: any) => {
        return <div className="row-container">{computeStatus(params)}</div>;
      },
      renderHeaderFilter: () => (
        <FieldPopoverGridFilter
          key={'status'}
          apiRef={apiRef}
          columnField={'status'}
          type={'options'}
          onApplyFilters={() =>
            onApplyFiltersDropdown(t('eca.exportManagement.status'))
          }
          dataArray={fieldTypeDataArray}
        />
      ),
    },
    {
      field: 'totalDocExported',
      headerName: t('eca.exportManagement.totalDocExported'),
      renderHeaderFilter: () => <span className="sr-only">{t('eca.exportManagement.totalDocExported')}</span>,      
      minWidth: 170,
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <AurDataGridCellWrapper
          cellData={String(params.value ?? '')}
          className="text-color-89A6FF"
        />
      ),
    },

    {
      field: 'exportFieldUsed',
      headerName: t('eca.exportManagement.tagFieldUsed'),
      minWidth: 221,
      flex: 1,
      sortable: true,
      renderCell: (params) => (
        <AurDataGridCellWrapper cellData={params.value} />
      ),
      renderHeaderFilter: (params) => (
        <CustomHeaderFilterCell params={params} apiRef={apiRef} />
      ),
    },
    {
      field: 'tagUsed',
      headerName: t('eca.exportManagement.tagUsed'),
      minWidth: 221,
      flex: 1,
      sortable: true,
      renderCell: (params) => (
        <AurDataGridCellWrapper cellData={params.value} />
      ),
      renderHeaderFilter: (params) => (
        <CustomHeaderFilterCell params={params} apiRef={apiRef} />
      ),
    },
    {
      field: 'fileSize',
      headerName: t('eca.exportManagement.fileSize'),
      renderHeaderFilter: () => <span className="sr-only">{t('eca.exportManagement.fileSize')}</span>, 
      minWidth: 170,
      flex: 1,
      sortable: true,
      filterable: false,
      renderCell: (params) => (
        <AurDataGridCellWrapper cellData={params.value} />
      ),
    },
    {
      field: 'filePath',
      headerName: t('eca.exportManagement.filePath'),
      minWidth: 300,
      flex: 1,
      sortable: true,
      renderCell: (params) => {
        const Value = params.value;
        const isPath = Value?.includes('/'); //  Show copy icon only if it's a path

        const exportPath = (
          <div className={isPath ? 'flex-center' : ''}>
            <Typography
              variant="body2"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1,
                ...(isPath && {width: '250px'}),
              }}
              title={Value}
            >
              {Value}
            </Typography>
            {params.row?.isLoadedToReviewPlatform ? (
              <Typography
                className="typo-text body4_size text-color-caution-300"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1,
                }}
                title={`Status changed by ${params.row.loadedMarkedBy} on ${params.row.loadedOnTime}`}
              >
                Status changed by {params.row.loadedMarkedBy} on{' '}
                {params.row.loadedOnTime}
              </Typography>
            ) : (
              Value == 'Load File Exported to Network Location' && (
                <Typography
                  onClick={(e) => ExportToClickHandler(e, params)}
                  className="typo-text body4_size text-color-8AA5FF"
                  data-testid="markAsLoadedLabel"
                >
                  {t('eca.exportManagement.markAsLoaded')}
                </Typography>
              )
            )}

            {isPath && (
              <AurTooltip
                title={t('eca.exportManagement.copyPath')}
                disableInteractive
                disableFocusListener
              >
                <IconButton
                  aria-label={t('eca.overlay.exportIconHint')}
                  onClick={(e) => handleCopyToClipboard(e, Value)}
                >
                  <AurSvgIcon src="images/copy.svg" ariaHidden />
                </IconButton>
              </AurTooltip>
            )}
          </div>
        );

        return (
          <AurDataGridCellWrapper customCellContent={exportPath} cellData="" />
        );
      },
      renderHeaderFilter: (params) => (
        <CustomHeaderFilterCell params={params} apiRef={apiRef} />
      ),
    },
    {
      field: 'exportDateTime',
      headerName: t('eca.exportManagement.exportDateTime'),
      minWidth: 221,
      flex: 1,
      sortable: true,
      type: 'date',
      filterOperators: getGridDateOperators().map((operator) => ({
        ...operator,
      })),
      valueGetter: (value) => {
        return new Date(value);
      },
      valueFormatter: (_value, row) => {
        return getUTCDateTimeForDisplay(row.exportDateTime);
      },
      renderCell: (param) => {
        return (
          <AurDataGridCellWrapper
            tooltipText={t('common.yearMonthDate')}
            cellData={param.formattedValue}
          />
        );
      },
      renderHeaderFilter: () => (
        <FieldPopoverGridFilter
          apiRef={apiRef}
          columnField={'exportDateTime'}
          type={'date'}
          onApplyFilters={() =>
            onApplyFiltersDropdown(t('eca.exportManagement.exportDateTime'))
          }
          key={'exportDateTime'}
        />
      ),
    },
    {
      field: 'exportedBy',
      headerName: t('eca.exportManagement.exportedBy'),
      minWidth: 209,
      flex: 1,
      sortable: true,
      renderCell: (params) => (
        <AurDataGridCellWrapper cellData={params.value} />
      ),
      renderHeaderFilter: (params) => (
        <CustomHeaderFilterCell params={params} apiRef={apiRef} />
      ),
    },
  ];

  const handleDownloadCsv = async () => {
    try {
      setIsDownloading(true);
      const params = `clientId=${clientId}&projectId=${selectedProjectId}&`;

      const response = await DownloadExportCsv(params);
      downloadResponseAsCSV(
        response,
        getProjectReportFileName(projectName, t('eca.exportManagement.exports'))
      );
      trackGAEvent({
        eventName: GA_EVENT_NAMES.FILE_DOWNLOAD,
        file_extension: GA_FILE_EXTENTIONS.CSV,
        click: {
          name: t('gaTracking.export.downloadCsv'),
          type: GA_CLICK_TYPES.BUTTON,
          region: GA_REGIONS.BODY,
          action: t('gaTracking.export.downloadedTotalExports', {
            count: totalCount,
          }),
        },
      });
    } catch (error: any) {
      console.error('Error downloading the CSV:', error?.message);
    } finally {
      setIsDownloading(false);
    }
  };

  const noDataGridOverlay = useMemo(() => {
    if (filterCount > 0 && rows.length <= 0) return AurDataGridNoRowsOverlay;
    return AurDataGridNoRowsOverlay;
  }, []) as React.JSXElementConstructor<unknown>;

  const projectSwitchHandler = useCallback(() => {
    dispatch(setModalStatus(true));
  }, [dispatch]);
  const handleRowClick = (data: any) => {
    dispatch(setExportId(data.exportId));
    dispatch(setExportName(data.exportName));
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('gaTracking.export.exports'),
        type: GA_CLICK_TYPES.LINK,
        region: GA_REGIONS.BODY,
        action: t('gaTracking.export.viewedExports'),
        details: t('gaTracking.export.exportStatus', {status: data.status}),
      },
    });
    navigate('/users/exportdetails');
  };
  const renderHeaderNButtons = useMemo(() => {
    return (
      <>
        <Typography
          className="typo-text display3_size overlay-title"
          data-testid="manage-overlay"
          component="h1"
        >
          {t('eca.exportManagement.labelExport')}
        </Typography>
        <Box className="button-wrapper">
          {hasExportPerm && (
            <AurButton
              classNames="typo-text buttonMedium_size"
              onClick={() => {
                dispatch(setExportTab1Params({}));
                dispatch(setSelectedExportId(''));
                trackGAEvent({
                  eventName: GA_EVENT_NAMES.CLICK,
                  click: {
                    name: t('gaTracking.export.newExport'),
                    type: GA_CLICK_TYPES.BUTTON,
                    region: GA_REGIONS.BODY,
                    action: t('gaTracking.export.newExportBegin'),
                  },
                });
                navigate('/eci-home-page/create-new-export');
              }}
              type="continue"
              size="Medium"
              startIcon={<AddIcon aria-hidden />}
              dataTestId="createNewExport"
              ariaLabel={t('eca.exportManagement.createNewExportHint')}
              name={t('eca.exportManagement.newExport')}
            >
              {t('eca.exportManagement.newExport')}
            </AurButton>
          )}
          {manageMenus?.length > 0 && (
            <AurDropdownButton
              elementID={'manageButton'}
              buttonText={t('eca.ecaHomepage.manage')}
              type="continue"
              aria-live="polite"
              size="Medium"
              iconType="Arrow"
              iconSize="medium"
              iconClassName="client-profile-details-dropdown-icon"
              menuDirectionLeft={false}
              menuItemHeight="medium"
              menuItemWidth="mediumLarge"
              menuItemTextSize="small"
              dropDownIconType={DropdownIconTypes.Chev}
              menuItems={manageMenus}
              hintText={t('eca.exportManagement.manageHintText')}
            >
              <AurIcon
                icon={IconNames.ChevronDown}
                color={IconColors.Custom}
                customClassName="client-profile-details-dropdown-icon"
              />
            </AurDropdownButton>
          )}
          <AurButton
            ariaLabel={t('eca.overlay.projectButtonHint')}
            classNames="typo-text buttonMedium_size"
            onClick={projectSwitchHandler}
            type="continue"
            size="Medium"
            startIcon={<ExchangeIcon aria-hidden />}
            dataTestId="openProjectButton"
            name={t('ecaIndexing.projectSwitcher')}
          >
            {t('reports.inTakeAuditTab.header.projectButton')}
          </AurButton>
          <ProjectSwitcherModal
            initialSelectedProjectId={selectedProjectId ?? undefined}
            title={t('common.chooseYourProject')}
            heading={t('eca.exportManagement.labelExport')}
          />
        </Box>
      </>
    );
  }, [
    dispatch,
    manageMenus,
    navigate,
    projectSwitchHandler,
    selectedProjectId,
    hasExportPerm,
  ]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };
  const handlePageSizeChange = (pageSize: number) => {
    setItemsPerPage(pageSize);
    setCurrentPage(1);
  };

  const handleSnackbarClose = () => {
    setExportSuccessPopup(false);
  };

  const handleCellKeyDown = (_: GridCellParams, event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === 'Space') {
      event.preventDefault(); // Prevent the default Enter behavior
      /**
       * On Press of enter key, find active row and fire the onclick event for it
       */
      const focusedRow = document.activeElement?.closest(
        '.MuiDataGrid-row'
      ) as HTMLDivElement;
      if (focusedRow) {
        focusedRow.click();
      }
    }
  };

  const renderPagination = useMemo(() => {
    return (
      <Box className="table-pagination-container">
        {
          <Box className={'table-pagination-control'}>
            <AurPagination
              totalItems={totalCount}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              perPageOptions={TABLE_PAGE_SIZE_OPTION}
              setItemsPerPage={handlePageSizeChange}
              onPageChange={handlePageChange}
              itemType={t('common.exports')}
            />
          </Box>
        }

        {filterCount > 0 ? (
          <>
            <Box>
              <AurInfographicSeperator seperatorIcon={'VerticalLine'} />
            </Box>
            <Box
              className={'apply-filter-button-container'}
              onClick={() => {
                apiRef.current?.setFilterModel({items: []});
                setFilterCount(0);
              }}
              data-testid={'apply-clear-filter'}
            >
              <AurButton
                type={'exit'}
                size={'Small'}
                startIcon={<AurIcon icon={IconNames.ClearFiltersIcon} aria-hidden="true" />}
                label={`${t('fieldManagement.fieldTable.clearFilters')} (${filterCount})`}
              />
            </Box>
          </>
        ) : null}
      </Box>
    );
  }, [totalCount, itemsPerPage, currentPage, filterCount, apiRef]);

  const exportPopupContent = () => {
    return (
      <div className="typo-text body2_size text-color-mist-1400">
        {t('eca.exportManagement.markAsExpopopup.content')}
      </div>
    );
  };

  return (
    <div
      className="export-management-container"
      role="region"
      aria-label={t('eca.exportManagement.manageExports')}
    >
      <BackgroundBlur aria-hidden="true" />
      <div className="overlay-layout">
        <Box
          className="breadcrumb-section overlay-breadcrumb"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <AurBreadcrumb items={breadCrumbItems} />
        </Box>

        <Typography
          data-testid="project-title"
          className="typo-text subhead3_size project-title"
        >
          {projectName}
        </Typography>
        <Box display={'flex'} justifyContent={'space-between'}>
          {renderHeaderNButtons}
        </Box>
      </div>

      {hasExportPerm === undefined ? (
        <AurLoadingIndicator
          overlay={true}
          isOpen={hasExportPerm == undefined}
        />
      ) : hasExportPerm ? (
        <Box className="grid-layout">
          <Box className="grid-header">
            <Box className="grid-title">
              <Typography className="typo-text subhead2_size">
                {' '}
                {t('eca.exportManagement.totalExports')}{' '}
              </Typography>
              <Typography className="typo-text body2_size total-count">
                ( {totalCount} ){' '}
              </Typography>
            </Box>
            <Box>
              <AurButton
                ariaDescription={t('eca.exportManagement.exportGridCSV')}
                ariaLabel={t('eca.exportManagement.downloadCsv')}
                data-testid="export-button"
                onClick={() => handleDownloadCsv()}
                startIcon={
                  isDownloading ? (
                    <div className="color-loader">
                      <AurLoadingIndicator
                        size={16}
                        className="progress-icon"
                        aria-hidden="true"
                      />
                    </div>
                  ) : (
                    <AurSvgIcon src="images/download.svg" />
                  )
                }
                disabled={isDownloading}
                type="exit"
                size="Medium"
                label={t('eca.exportManagement.downloadCsv')}
                classNames="typo-text buttonMedium_size export-btn"
              />
            </Box>
          </Box>
          <Box className="table-container" sx={{bottom: '8px'}}>
            {renderPagination}
            <DataGridPro
              apiRef={apiRef}
              rows={rows}
              columns={columns}
              disableColumnSorting={false}
              className="listing-data-table aur-grid-pro-export"
              initialState={{
                pagination: {
                  paginationModel: {
                    pageSize: 200,
                  },
                },
              }}
              checkboxSelection={false}
              getRowId={(row) => row.id}
              rowSelectionModel={selectionModel} // Set selection model
              onRowSelectionModelChange={(newSelection) =>
                setSelectionModel(newSelection as string[])
              }
              onCellKeyDown={handleCellKeyDown}
              onRowClick={(params: {row: any}) => handleRowClick(params?.row)}
              disableColumnMenu
              headerFilters
              disableVirtualization
              hideFooter
              columnHeaderHeight={48}
              headerFilterHeight={52}
              rowHeight={50}
              filterDebounceMs={1000}
              getDetailPanelHeight={() => 'auto'}
              slots={{
                headerFilterMenu: null,
                noRowsOverlay: noDataGridOverlay,
                noResultsOverlay: noDataGridOverlay,
              }}
              slotProps={{
                headerFilterCell: {
                  showClearIcon: false,
                  InputComponentProps: {
                    type: 'search',
                    label: '',
                    placeholder: t('fieldManagement.fieldTable.search'),
                    autoComplete: 'off',
                  },
                  tabIndex: 0,
                },
              }}
              sx={{
                '& .MuiDataGrid-filler': {
                  height: '12px !important',
                  minHeight: '12px !important',
                  flex: '0 !important',
                  marginBottom: '10px !important',
                },
              }}
              filterMode="server"
            />
            {renderPagination}
          </Box>
        </Box>
      ) : (
        <SecurePage />
      )}

      <AurToast
        ariaLabel={t('common.toastAlert.toastAlertHint')}
        hideDuration={5000}
        open={exportSuccessPopup}
        horizontalPosition={'center'}
        message={`${createExportName} ${t('eca.exportManagement.exportInProgress')}`}
        onClose={() => handleSnackbarClose}
      />
      <AurPopup
        className="aur-unsaved-changes-popup"
        data-testid="markExportPopup"
        open={showExportedPopup}
        onOk={handlePopupContinue}
        onCancel={closePopupModalHandler}
        showPopupHeading={true}
        popupHeading={t('eca.exportManagement.markAsExpopopup.heading')}
        popupTitle={t(`common.justToConfirm`)}
        popupContent={exportPopupContent()}
        contentType="element"
        headingIcon={<HeadingIcon />}
        showPopupTitle={true}
        showCloseIcon={false}
        labelActionCancel={t('common.clearAllPopup.buttonCancel')}
        labelActionOk={t(`common.yes`)}
        exitButtonHint={t('common.clearAllPopup.cacnelToClearHint')}
        continueButtonHint={t('indexes.reRunPopup.confirmToClearHint')}
        cancelButtonTitle=""
        titleCustomClass="label2_size"
      />
      <AurLoadingIndicator overlay={true} isOpen={reqLoading} />
    </div>
  );
};

export default ExportManagementLanding;
